import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import { classNames } from '@resurs-internal/styx-react/lib/utils/helpers'
import {
  Button,
  ButtonSize,
  ButtonType,
  Modal,
  Toggle,
} from '@resurs-internal/styx-react'
import { Card } from './Card'
import { PageContext } from './ContentContext'
import { Login } from './Login'
import { GizmoType, PageEditor, PageStatus } from './PageEditor'
import { actOnData, joinStrings } from '../../common/utils'
import stack from '../../assets/stack.svg'
import side from '../../assets/side.svg'
import commonStyles from './styles.module.scss'
import styles from './EditPagesRoute.module.scss'
import 'react-toastify/dist/ReactToastify.css'

const MESSAGE_SOURCE_FLOW_UI_PORTAL = 'flow-ui-portal'
const MESSAGE_SOURCE_FLOW_UI_MOCK_HANDLER = 'flow-ui-mock-handler'

const PortalPostMessageEvents = {
  INIT_MOCKS: 'INIT_MOCKS',
  REQUEST: 'REQUEST',
  RESPONSE: 'RESPONSE',
  MOCK_EVENT: 'MOCK_EVENT',
  RESET_MOCK: 'RESET_MOCK',
  WINDOW: 'WINDOW',
  PING: 'PING',
  INIT: 'INIT',
  OPEN: 'OPEN',
}

const postMessage = (target, type, value) => {
  window.parent.postMessage(
    { src: MESSAGE_SOURCE_FLOW_UI_MOCK_HANDLER, type, value },
    '*',
  )
}

const mockUrl = (baseUrl, page, countryCode) => mockId => ({
  url: `${baseUrl}/test/mock?mocked=${mockId}${
    countryCode && `&c=${countryCode}`
  }&onNext=${encodeURIComponent(page)}`,
  mockId,
})

const enrichWithScenarioId = page => {
  const scenarioId = page.id
  const mockId = `${page.id}_m`
  const { changelog, content, ...rest } = page
  const eventMocks = content
    .filter(({ type }) => type === GizmoType.EVENT_MOCK)
    .map(data => ({ ...data, mockId: `${data.props.id}_m` }))
    .map(data =>
      JSON.parse(JSON.stringify(data).replaceAll('$flow_id', data.props.id)),
    )
  const requestMocks = content
    .filter(({ type }) => type === GizmoType.REQUEST_MOCK)
    .map(data =>
      (
        data.props.activeEvents?.split(',') ||
        eventMocks.map(({ props: { id: eventId } }) => eventId)
      ).map(eventId =>
        JSON.parse(JSON.stringify(data).replaceAll('$flow_id', eventId)),
      ),
    )
    .reduce((r, c) => [...r, ...c], [])
  return {
    ...rest,
    content: [...eventMocks, ...requestMocks],
    scenarioId,
    mockId,
    mockIds: eventMocks.map(data => data.mockId),
  }
}

const width = 480
const height = 960

const mockEventMap = {}

const parseContent = gizmo => {
  try {
    return actOnData(gizmo.props?.data, data => JSON.parse(data))
  } catch (e) {
    return null
  }
}

const getEventMocks = page =>
  page?.content
    ?.filter(gizmo => gizmo.type === GizmoType.EVENT_MOCK)
    .map(gizmo => {
      const data = parseContent(gizmo)
      return {
        gizmo,
        data,
        countryCode: data?.object?.countryCode,
        customerType: data?.object?.customerType,
      }
    })

export const EditPagesRoute = () => {
  const { logout, me, pages: contextPages } = useContext(PageContext)
  const [pages, setPages] = useState([])
  const [parsedPages, setParsedPages] = useState({})
  const [selectedPage, selectPage] = useState()
  const [flowUrl, setFlowUrl] = useState()
  const flowRef = useRef()
  const [mockMisses, setMockMisses] = useState([])
  const [showModal, setShowModal] = useState(true)
  const [activeMocks, setActiveMocks] = useState([])
  const [focusedMock, setFocusedMock] = useState()
  const [multiMode, setMultiMode] = useState(false)
  const [showLabels, setShowLabels] = useState(false)

  useEffect(() => {
    setPages([
      ...(contextPages || [])
        .filter(({ status }) => status !== PageStatus.ARCHIVED)
        .sort((a, b) => a.order - b.order)
        .map(enrichWithScenarioId),
    ])
  }, [contextPages])

  const setMockEvent = (mockId, eventId) => {
    mockEventMap[mockId] = eventId
  }

  const getMockedResponse = (url, requestInfo, mockId) => {
    const mockedResponse = pages
      ?.flatMap(({ content }) => content)
      .filter(({ type }) => type === GizmoType.REQUEST_MOCK)
      .find(
        ({ props: { path, method, activeEvents = '' } = {} }) =>
          (!activeEvents || activeEvents.includes(mockEventMap[mockId])) &&
          requestInfo?.method === method &&
          new RegExp(path).test(url),
      )
    if (mockedResponse) {
      return {
        status: +mockedResponse.props.status,
        data: JSON.parse(mockedResponse.props.data),
        delay: +mockedResponse.props.delay || 0,
      }
    }
    return null
  }

  const updateFocus = (mockId, focused) => {
    setFocusedMock(prevState => {
      let nextFocus = prevState
      if (focused) {
        nextFocus = mockId
      } else if (prevState === mockId) {
        nextFocus = null
      }
      return nextFocus
    })
  }

  const mockRequest = ({ url, requestInfo, id, mockId }) => {
    const mockedResponse = getMockedResponse(url, requestInfo, mockId)
    if (!mockedResponse) {
      setMockMisses(prev => [{ url, requestInfo, id }, ...prev])
    }
    setTimeout(
      () =>
        postMessage(
          flowRef.current?.contentWindow,
          PortalPostMessageEvents.RESPONSE,
          {
            response: mockedResponse || {
              status: 404,
              data: {
                code: 'NOT_FOUND',
                message: 'Mock not found',
              },
            },
            id,
            mockId,
          },
        ),
      mockedResponse?.delay || 0,
    )
  }

  const handleMessage = useMemo(
    () => e => {
      if (e.data.src !== MESSAGE_SOURCE_FLOW_UI_PORTAL) {
        return
      }
      console.info('PROXY message', e.data)
      switch (e.data.type) {
        case PortalPostMessageEvents.REQUEST: {
          mockRequest(e.data.value)
          break
        }
        case PortalPostMessageEvents.INIT_MOCKS:
          postMessage(
            flowRef.current?.contentWindow,
            PortalPostMessageEvents.INIT_MOCKS,
            {
              mockId: e.data.value.mockId,
              paths: ['.*'],
            },
          )
          break
        case PortalPostMessageEvents.WINDOW: {
          const { focus, blur, close, mockId } = e.data.value
          if (focus) {
            updateFocus(mockId, true)
          }
          if (blur) {
            updateFocus(mockId, false)
          }
          if (close) {
            setActiveMocks(prevState => [
              ...prevState.filter(mock => mock !== mockId),
            ])
          }
          break
        }
        case PortalPostMessageEvents.PING: {
          const { mockId } = e.data.value
          setActiveMocks(prevState => [
            ...prevState.filter(mock => mock !== mockId),
            mockId,
          ])
          break
        }
        case PortalPostMessageEvents.INIT: {
          setFlowUrl(e.origin)
          break
        }
        default:
          console.debug('Unhandled type:', e.data.type)
          break
      }
    },
    [pages],
  )

  const openWindow = (url, mockId, features) => {
    postMessage(flowRef.current?.contentWindow, PortalPostMessageEvents.OPEN, {
      mockId,
      url,
      features,
    })
  }

  const closeWindow = mockId => {
    postMessage(
      flowRef.current?.contentWindow,
      PortalPostMessageEvents.WINDOW,
      {
        mockId,
        close: true,
      },
    )
  }

  const toggleLabels = mockId => {
    postMessage(
      flowRef.current?.contentWindow,
      PortalPostMessageEvents.WINDOW,
      {
        mockId,
        toggleLabels: showLabels,
      },
    )
  }

  const moveWindow = (mockId, { x, y }) => {
    postMessage(
      flowRef.current?.contentWindow,
      PortalPostMessageEvents.WINDOW,
      {
        mockId,
        move: {
          x,
          y,
        },
        resize: {
          width,
          height,
        },
      },
    )
  }

  const focusWindow = mockId => {
    postMessage(flowRef.current?.contentWindow, PortalPostMessageEvents.OPEN, {
      mockId,
      url: '',
    })
  }

  const pingWindow = mockId => {
    postMessage(flowRef.current?.contentWindow, PortalPostMessageEvents.PING, {
      mockId,
    })
  }

  const arrangeStack = () => {
    const { availHeight: screenHeight, availTop, availLeft } = window.screen
    activeMocks.reduce(
      ({ x, y }, mockId) => {
        focusWindow(mockId)
        moveWindow(mockId, { x, y })
        const nextX = x + 40
        const nextY = y + 70
        const moveX = nextY + height > screenHeight
        return {
          x: moveX ? x + width : nextX,
          y: moveX ? availTop + 40 : nextY,
        }
      },
      {
        x: availLeft,
        y: availTop + 40,
      },
    )
  }

  const arrangeSideBySide = () => {
    const { width: screenWidth, availTop, availLeft } = window.screen
    activeMocks.reduce(
      ({ x, y }, mockId) => {
        focusWindow(mockId)
        moveWindow(mockId, { x, y })
        const nextX = x + width
        const moveY = nextX > screenWidth
        return {
          x: moveY ? availLeft || 0 : nextX,
          y: moveY ? y + height : y,
        }
      },
      { x: availLeft, y: availTop },
    )
  }

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    postMessage(flowRef.current?.contentWindow, PortalPostMessageEvents.INIT)
    pages.forEach(page => {
      pingWindow(page.mockId)
      page.mockIds?.forEach(pingWindow)
    })
    return () => window.removeEventListener('message', handleMessage)
  }, [pages])

  useEffect(() => {
    activeMocks.forEach(mockId => toggleLabels(mockId))
  }, [showLabels])

  useEffect(() => {
    setParsedPages(
      pages.reduce((r, c) => ({ ...r, [c.id]: getEventMocks(c) }), {}),
    )
  }, [pages])

  const renderEditView = () => {
    if (!flowUrl) {
      return <p>Initializing...</p>
    }

    if (!me) {
      return <Login />
    }

    return (
      <>
        <div
          className={classNames(
            commonStyles.flexRow,
            commonStyles.spaceBetween,
            styles.toolbar,
          )}
        >
          <div className={commonStyles.flexRow}>
            <Button
              id="reorganize"
              iconSvg={side}
              disabled={!activeMocks.length}
              onClick={arrangeSideBySide}
            >
              Side-by-side
            </Button>
            <Button
              id="stack"
              iconSvg={stack}
              disabled={!activeMocks.length}
              onClick={arrangeStack}
            >
              Stack
            </Button>
            <Button
              id="close-all"
              type={ButtonType.Secondary}
              disabled={!activeMocks.length}
              onClick={() => activeMocks.forEach(mockId => closeWindow(mockId))}
            >
              Close all
              {!!activeMocks.length && ` (${activeMocks.length})`}
            </Button>
            <Toggle
              id="toggle_multi"
              checked={multiMode}
              onChange={() => {
                setMultiMode(!multiMode)
              }}
            >
              Multi mode
            </Toggle>
            <Toggle
              id="toggle_labels"
              checked={showLabels}
              onChange={() => {
                setShowLabels(!showLabels)
              }}
            >
              Labels
            </Toggle>
          </div>
          <div className={commonStyles.flexRow}>
            <p>Logged in as: {me?.name}</p>
            <Button
              id="logout-button"
              type="secondary"
              size={ButtonSize.Small}
              onClick={logout}
            >
              Log out
            </Button>
          </div>
        </div>
        {pages?.map(page => (
          <Card
            key={page.id}
            header={
              <div
                className={classNames(
                  commonStyles.flexRow,
                  commonStyles.spaceBetween,
                )}
              >
                {page.name}
                <div className={styles.buttonContainer}>
                  <Button
                    id={`edit_${page.id}`}
                    type="secondary"
                    outline
                    onClick={() => {
                      selectPage(page)
                      setShowModal(true)
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            }
            subheader=" "
            className={
              focusedMock === page.mockId
                ? styles.focusedCard
                : activeMocks.includes(page.mockId) && styles.activeCard
            }
          >
            <div className={styles.page}>
              <div className={commonStyles.flexRow}>
                {parsedPages[page.id]?.map(({ gizmo, countryCode }) => (
                  <Button
                    id={`gizmo-${gizmo.props?.id}`}
                    key={gizmo.props?.id}
                    size={ButtonSize.Small}
                    outline
                    onClick={() => {
                      const mockId = multiMode
                        ? `${gizmo.props.id}_m`
                        : page.mockId
                      const scenarioId = gizmo.props.id
                      const flowMockPage = `/test/flow/${scenarioId}?mocked=${mockId}$&c=${countryCode}`
                      const { url } = mockUrl(
                        flowUrl,
                        flowMockPage,
                        countryCode,
                      )(mockId)
                      setMockEvent(mockId, gizmo.props.id)

                      if (activeMocks.includes(mockId)) {
                        focusWindow(mockId)
                      } else {
                        openWindow(
                          url,
                          mockId,
                          `width=${width}px,height=${height}px`,
                        )
                      }

                      postMessage(
                        flowRef.current?.contentWindow,
                        PortalPostMessageEvents.RESET_MOCK,
                        {
                          id: scenarioId,
                          mockId,
                          url: flowMockPage,
                        },
                      )

                      const dataString = gizmo.props.data
                        ?.replaceAll('$flow_id', scenarioId)
                        ?.replaceAll('$mock_id', mockId)
                        ?.replaceAll('$event_id', gizmo.props.id)
                      const { type, object } = JSON.parse(dataString) || {}
                      postMessage(
                        flowRef.current?.contentWindow,
                        PortalPostMessageEvents.MOCK_EVENT,
                        {
                          id: scenarioId,
                          eventType: type,
                          data: object,
                        },
                      )
                      if (showLabels) {
                        setTimeout(() => toggleLabels(mockId), 1000)
                      }
                    }}
                  >
                    {joinStrings(
                      countryCode,
                      gizmo.props?.description?.replace(countryCode, '').trim(),
                    )(' - ')}
                  </Button>
                ))}
              </div>
            </div>
          </Card>
        ))}
        <Button
          id="add_page"
          onClick={() => {
            selectPage({ status: 'DRAFT', content: [], language: 'en' })
            setShowModal(true)
          }}
        >
          Add page
        </Button>
        {mockMisses.map(({ id, url, requestInfo }) => (
          <div key={id}>
            <small>
              <code>{requestInfo?.method}: </code>
              <code>{url}</code>
              {requestInfo.body && (
                <>
                  <br />
                  <code>{requestInfo.body}</code>
                </>
              )}
            </small>
          </div>
        ))}
        {!!mockMisses.length && (
          <Button
            id="clear-requests"
            type={ButtonType.Secondary}
            onClick={() => setMockMisses([])}
          >
            Clear requests
          </Button>
        )}
        {selectedPage && showModal && (
          <Modal
            onClose={() => setShowModal(false)}
            header={
              selectedPage.id
                ? `Edit scenario: ${selectedPage.name}`
                : 'Add scenario'
            }
          >
            <PageEditor
              pageId={selectedPage.id}
              onClose={() => setShowModal(false)}
            />
          </Modal>
        )}
      </>
    )
  }

  return (
    <div className={classNames('styx', styles.wrapper)}>
      <ToastContainer />
      {renderEditView()}
    </div>
  )
}
